import { useEffect, useState, useMemo } from "react";
import { Music2, X, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * DESIGN-003 §9.3 — one-time migration confirmation modal.
 *
 * Fires whenever the current user has either:
 *   - `profile_migration.primary_instrument_missing === true`, OR
 *   - `profile_migration.pending_review_instruments` is non-empty.
 *
 * User picks a primary instrument (required). For each remaining pending
 * item they choose "keep as doubling" (only when valid) or "remove".
 *
 * Never blocks the app — the modal is dismissible but the same prompt
 * reappears until the user resolves it.
 */
export const SCOPE_OPEN_EVENT = "amds:open-musical-scope";
const laterKey = (u) => `amds_scope_later_${u?.id || u?._id || u?.email || ""}`;

export default function MigrationConfirmationModal() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [catalogue, setCatalogue] = useState(null);
  const [primary, setPrimary] = useState("");
  const [dispositions, setDispositions] = useState({}); // {"Piano": "double" | "remove"}
  const [saving, setSaving] = useState(false);
  const [forced, setForced] = useState(false);

  const mig = user && typeof user === "object" ? user.profile_migration : null;
  const shouldShow = !!(mig && mig.needs_prompt);
  // "Later" is remembered for this browser session (Layout remounts on every page).
  const laterThisSession = typeof window !== "undefined" && !!user && window.sessionStorage.getItem(laterKey(user)) === "1";

  // Context-aware copy:
  //   New user   = primary instrument missing AND no legacy entries carried over
  //   Migrated user = has one or more legacy entries still to review
  const isMigrated = !!(mig && (mig.pending_review_instruments || []).length > 0);

  // Don't gate the /contacts page — user might want to import contacts even
  // before completing their own profile. Same for the Import Contacts modal.
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const suppressOnRoute = pathname.startsWith("/contacts");

  useEffect(() => {
    setOpen(shouldShow && (forced || (!suppressOnRoute && !laterThisSession)));
  }, [shouldShow, suppressOnRoute, laterThisSession, forced]);

  useEffect(() => {
    const onOpen = () => setForced(true);
    window.addEventListener(SCOPE_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SCOPE_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open || catalogue) return;
    (async () => {
      try {
        const { data } = await api.get("/instruments/catalogue");
        setCatalogue(data);
      } catch { /* silent — modal will show a friendly error */ }
    })();
  }, [open, catalogue]);

  const pending = mig?.pending_review_instruments || [];

  // Once a primary is chosen, work out which of the remaining pending values
  // are valid doublings for that primary.
  const validDoublingsForPrimary = useMemo(() => {
    if (!primary || !catalogue) return new Set();
    const map = catalogue.recognised_doublings || {};
    const universal = map["*"] || [];
    const forPrimary = map[primary] || [];
    return new Set([...universal, ...forPrimary]);
  }, [primary, catalogue]);

  // Auto-suggest the first pending item as primary once catalogue loads.
  useEffect(() => {
    if (!primary && pending.length > 0 && catalogue) {
      const known = pending.find((p) =>
        (catalogue.instruments || []).some((i) => i.canonical === p)
      );
      if (known) setPrimary(known);
    }
  }, [pending, catalogue, primary]);

  const setDisposition = (name, choice) => {
    setDispositions((d) => ({ ...d, [name]: choice }));
  };

  const canSubmit = () => {
    if (!primary) return false;
    for (const p of pending) {
      if (p === primary) continue;
      if (!dispositions[p]) return false;
    }
    return true;
  };

  const submit = async () => {
    if (!canSubmit()) {
      toast.error("Please choose your primary instrument and resolve every entry.");
      return;
    }
    setSaving(true);
    try {
      const doublings = [];
      for (const p of pending) {
        if (p === primary) continue;
        if (dispositions[p] === "double") doublings.push(p);
      }
      await api.put("/profile/me/musical-scope", {
        primary_instrument: primary,
        recognised_doublings: doublings,
        section_responsibilities: [],
        resolve_pending: true,
      });
      toast.success("Profile updated");
      await refresh();
      setForced(false);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const dismiss = () => {
    window.sessionStorage.setItem(laterKey(user), "1");
    setForced(false);
    setOpen(false);
  };

  if (!open || !mig) return null;

  const instrumentOptions = catalogue?.instruments || [];

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-ink-900/50 p-2"
      onClick={dismiss}
      data-testid="migration-modal"
    >
      <div
        className="bg-white w-full max-w-lg rounded-2xl sm:rounded-3xl shadow-card max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 pb-4 border-b border-ivory-200">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="label-overline inline-flex items-center gap-1.5"><Music2 size={11} /> Profile check</div>
              <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">
                Confirm your musical scope
              </h2>
            </div>
            <button
              type="button"
              onClick={dismiss}
              className="btn-ghost !p-1.5"
              aria-label="Close"
              data-testid="migration-modal-close"
            >
              <X size={18} />
            </button>
          </div>
          <p className="text-sm text-ink-600 mt-3" data-testid={isMigrated ? "migration-copy-migrated" : "migration-copy-new"}>
            {isMigrated ? (
              <>
                Please tell us which instrument best represents your main musical practice.
                We have preserved your existing profile information, but we need you to
                confirm how each entry should be treated.
              </>
            ) : (
              <>
                Choose the instrument or discipline that best represents your main musical
                practice. This helps AMDS show the right opportunities and availability
                to other musicians.
              </>
            )}
          </p>
        </div>

        <div className="p-6 space-y-5">
          {/* Primary instrument */}
          <div>
            <label className="label-overline block mb-2">Primary instrument (required)</label>
            <select
              className="input-field w-full"
              value={primary}
              onChange={(e) => setPrimary(e.target.value)}
              data-testid="migration-primary-select"
            >
              <option value="">— Choose your main instrument —</option>
              {/* Pending values first so the user can pick from what they had */}
              {pending
                .filter((p) => instrumentOptions.some((i) => i.canonical === p))
                .map((p) => (
                  <option key={`pending-${p}`} value={p}>{p} (was on your profile)</option>
                ))}
              <optgroup label="All instruments">
                {instrumentOptions.map((i) => (
                  <option key={i.canonical} value={i.canonical}>{i.canonical}</option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* Disposition per pending item */}
          {pending.length > 0 && (
            <div className="space-y-3" data-testid="migration-pending-list">
              <div className="label-overline">Other entries from your old profile</div>
              {pending.filter((p) => p !== primary).map((p) => {
                const known = instrumentOptions.some((i) => i.canonical === p);
                const doublingAllowed = known && primary && validDoublingsForPrimary.has(p);
                return (
                  <div key={p} className="p-3 rounded-xl bg-ivory-100 border border-ivory-200">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium">{p}{!known && <span className="ml-2 text-xs text-ink-500">(not in our catalogue)</span>}</div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => setDisposition(p, "double")}
                        disabled={!doublingAllowed}
                        title={!doublingAllowed ? "Not a recognised doubling of your primary" : ""}
                        className={`pill ${dispositions[p] === "double" ? "bg-sage-500 text-white" : "bg-sage-100 text-sage-700"} ${!doublingAllowed ? "opacity-40 cursor-not-allowed" : ""}`}
                        data-testid={`migration-double-${p}`}
                      >
                        Keep as recognised doubling
                      </button>
                      <button
                        type="button"
                        onClick={() => setDisposition(p, "remove")}
                        className={`pill ${dispositions[p] === "remove" ? "bg-ink-800 text-white" : "bg-ivory-200 text-ink-700"}`}
                        data-testid={`migration-remove-${p}`}
                      >
                        Remove from profile
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="p-3 rounded-xl bg-ivory-100 text-xs text-ink-600 inline-flex items-start gap-2">
            <ShieldCheck size={14} className="text-sage-600 mt-0.5 shrink-0" />
            <span>You can add or remove recognised doublings and section responsibilities from your profile at any time.</span>
          </div>
        </div>

        <div className="p-6 pt-2 flex items-center justify-end gap-3 border-t border-ivory-200">
          <button
            type="button"
            onClick={dismiss}
            className="btn-ghost text-sm"
            data-testid="migration-later"
          >
            Later
          </button>
          <button
            type="button"
            disabled={!canSubmit() || saving}
            onClick={submit}
            className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="migration-submit"
          >
            {saving ? "Saving…" : "Confirm and save"}
          </button>
        </div>
      </div>
    </div>
  );
}
